import { CreateCGSchemaRequest, ZipApi } from '../../lib/ZipApi';
import { directoryStore } from '../DirectoryStore';
import { CGBaseTypeDefinition, CGContentType, CGObjectDefinition, CGProductInstance, CGSourceMetadata, ObjectDefinition, OptimizelyProduct } from '../../types/DataSyncItem';
import { DataSyncObjectsStore } from './DataSyncObjectsStore';
import { showToasts } from '../../lib/toaster';
import { CGLanguage } from '../../types/ContentGraph';
import { action, observable } from 'mobx';
import { DataSyncObjectDefinitionService } from '../../services/DataSync/DataSyncObjectDefinitionService';
import { CGDestinationSchema, DestinationSchemaItem } from '../../types/Schema';
import { FieldDefinition } from '../../types/FieldDefinition';
import { convertSchemaV2ToObjectDefinition } from '../../lib/convertV2Schema';
import { getApiErrorMessage } from '../../lib/ZipApiError';

export default class DataSyncCgObjectsStore extends DataSyncObjectsStore {

  @observable public loadingSourceMetadata = false;
  @observable public loadingBaseTypes = false;
  @observable public savingNewLanguage = false;

  public preset: string | null = null;
  public productInstance: CGProductInstance | null = null;
  @observable private _destinationObjectList?: DestinationSchemaItem[];
  @observable private sourceMetadata?: CGSourceMetadata;
  @observable private baseTypes: CGBaseTypeDefinition[] = [];

  @action
  public async fetchSourceMetadata() {
    if (!this.instanceId || !directoryStore.trackerId) {
      return;
    }

    try {
      this.loadingSourceMetadata = true;
      this.sourceMetadata = undefined;
      this.sourceMetadata = await ZipApi.getCGSourceMetadata(directoryStore.trackerId, this.instanceId);
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to fetch source languages');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.loadingSourceMetadata = false;
    }
  }

  @action
  public async fetchBaseTypes() {
    if (!this.instanceId || !directoryStore.trackerId) {
      return;
    }

    try {
      this.loadingBaseTypes = true;
      this.baseTypes = [];
      this.baseTypes = await ZipApi.listCGBaseTypes(directoryStore.trackerId, this.instanceId);
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to fetch graph base types');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.loadingBaseTypes = false;
    }
  }

  public getSourceMetadata(): CGSourceMetadata | undefined {
    return this.sourceMetadata;
  }

  public getBaseTypeDefinitions(): CGBaseTypeDefinition[] {
    return this.baseTypes;
  }

  @action
  public async updateSourceMetadata(additionalLanguages: [CGLanguage]) {
    if (!this.instanceId || !directoryStore.trackerId) {
      return;
    }
    try {
      this.savingNewLanguage = true;
      await ZipApi.updateCGSourceMetadata(directoryStore.trackerId, this.instanceId, additionalLanguages);
      const existingLanguages = this.sourceMetadata?.languages ? this.sourceMetadata.languages : [];
      this.sourceMetadata = {languages: existingLanguages.concat(additionalLanguages)};
    } finally {
      this.savingNewLanguage = false;
    }
  }

  @action
  public async persistTransientDestinationObject(): Promise<void> {
    if (!this._transientDestinationObject) {
      return;
    }

    const contentType =  DataSyncObjectDefinitionService.convertToCGContentType(
      this._transientDestinationObject as CGObjectDefinition
    ) as CGContentType;

    const request = {
      'content_type': contentType,
      'preset': this.preset ? this.preset : 'NEXT',
      'tracker_id': directoryStore.trackerId,
      'instance_id': this.instanceId
    } as CreateCGSchemaRequest;

    await ZipApi.saveCGSchema(request);

    if (!this._destinationObjectDefinitions) this._destinationObjectDefinitions = [];

    const existingIndex = this._destinationObjectDefinitions.findIndex(
      (obj) => obj.object_name === this._transientDestinationObject!.object_name
    );

    if (existingIndex !== -1) {
      this._destinationObjectDefinitions[existingIndex] = this._transientDestinationObject;
    } else {
      this._destinationObjectDefinitions.push(this._transientDestinationObject);
    }

    this.setTransientDestinationObject(undefined);
  }

  public getProductInstance(): CGProductInstance {
    if (this.productInstance) return this.productInstance;
    return {} as CGProductInstance;

  }

  public getInstanceId(): string | undefined {
    return this.instanceId;
  }

  public getDestinationObjectList(): ObjectDefinition[] {
    if (!this._destinationObjectList) {
      return [];
    }
    return this._destinationObjectList.map((item) => {
      return {
        object_name: item.id,
        object_display_name: item.id || item.display_name,
        object_icon_url: '',
        fields: [],
      };
    });
  }

  public async getSourceObjectFields(objectName: string): Promise<FieldDefinition[]> {
    const object = this._sourceObjectDefinitions?.find((obj) => obj.object_name === objectName);
    if (!object) {
      return [];
    }
    if (object.fields.length > 0) {
      return object.fields;
    }

    if (!directoryStore.trackerId || !this.instanceId || !objectName) {
      return [];
    }

    this.loading = true;
    try {
      const schema = await ZipApi.getSourceSchema(
        directoryStore.trackerId,
        OptimizelyProduct.CGR,
        this.instanceId,
        objectName
      );
      const objectDef = convertSchemaV2ToObjectDefinition(schema);
      if (objectDef?.fields) {
        object.fields = objectDef.fields;
        return objectDef.fields;
      }
    } catch (error: any) {
      await this.handleFetchError(error);
    } finally {
      this.loading = false;
    }

    return [];
  }

  public async getDestinationObjectFields(objectName: string): Promise<FieldDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId || !objectName) {
      return [];
    }

    const transientDestinationObject = this.getTransientDestinationObject();
    if (transientDestinationObject?.object_name === objectName) {
      return transientDestinationObject?.fields || [];
    }

    this.loading = true;
    try {
      const schema = await ZipApi.getDestinationSchema(directoryStore.trackerId, 'CGR', this.instanceId, objectName);
      const objectDef = convertSchemaV2ToObjectDefinition(schema);
      if (objectDef?.fields) {
        return objectDef.fields;
      }
    } catch (error: any) {
      await this.handleFetchError(error);
    } finally {
      this.loading = false;
    }

    return [];
  }

  public async getDestinationObjectSchema(objectName: string): Promise<CGDestinationSchema | undefined> {
    if (!directoryStore.trackerId || !this.instanceId || !objectName) {
      return undefined;
    }

    this.loading = true;
    try {
      const schema = await ZipApi.getDestinationSchema(directoryStore.trackerId, 'CGR', this.instanceId, objectName);
      return schema;
    } catch (error: any) {
      await this.handleFetchError(error);
    } finally {
      this.loading = false;
    }

    return undefined;
  }

  protected async fetchSourceObjectDefinitions(): Promise<ObjectDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId) {
      return [];
    }
    const response = await ZipApi.listSourceSchemas(
      directoryStore.trackerId,
      OptimizelyProduct.CGR,
      this.instanceId
    );
    if (!response?.schemas) {
      return [];
    }
    return response.schemas.map((item) => ({
      object_name: item.id,
      object_display_name: item.display_name || item.id,
      object_icon_url: '',
      fields: [],
      disabled: item.disabled,
      warning: item.warning,
    }));
  }

  protected async fetchDestinationObjectDefinitions(): Promise<ObjectDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId) {
      return [];
    }
    const response = await ZipApi.listDestinationSchemas(directoryStore.trackerId, 'CGR', this.instanceId);
    if (response) {
      this._destinationObjectList = response.schemas || [];
    }
    return this.getDestinationObjectList();
  }

  protected async handleFetchError(error: any): Promise<void> {
    const message = await getApiErrorMessage(error, 'Unable to fetch graph schema');
    showToasts([{intent: 'danger', message}]);
  }

  protected async fetchAdditionalData() {
    await Promise.all([
      this.fetchProductInstance(),
      this.fetchSourceMetadata(),
      this.fetchBaseTypes()
    ]);
  }

  private async fetchProductInstance(): Promise<void> {
    if (directoryStore.trackerId && this.instanceId) {
      this.productInstance = await ZipApi.getCGInstance(directoryStore.trackerId, this.instanceId);
    }
  }
}
